import React, { FC } from 'react';
import {
  BasicContainer,
  BodyText,
  BodyTextLarge,
  ButtonProps,
  FlexCol,
  FlexRow,
  GridDynamic,
  TextDynamic,
} from '@minecraft.atoms';
import Icon from '@minecraft.icon';
import Modal from '@minecraft.modal';

type FooterItem = {
  icon: string;
  title: string;
  description: React.ReactNode;
  key: string;
};

const FooterItemTemplate = ({ item }: { item: FooterItem }) => {
  return (
    <FlexCol w="full" data-testid="footer-item">
      <FlexRow w="full" gap="2" alignItems="baseline">
        <Icon name={item.icon} size="sm" />
        <BodyTextLarge fontWeight="semibold">{item.title}</BodyTextLarge>
      </FlexRow>
      <BodyText>{item.description}</BodyText>
    </FlexCol>
  );
};

const FeatureListItemTemplate = ({ item }: { item: React.ReactNode }) => (
  <FlexRow wrap="nowrap" role="listitem" gap="4" data-testid="social-feature-item">
    <BasicContainer>
      <Icon name="diamondoutline" themeColor="success" size="md" />
    </BasicContainer>
    <BodyText>{item}</BodyText>
  </FlexRow>
);

export type SocialConsentModalProps = {
  isOpen?: boolean;
  close?: () => void;
  buttonsConfig?: ButtonProps[];
  platformIcon?: string;
  title?: string;
  description?: string;
  previewImage?: {
    src: string;
    srcSet?: string;
    alt: string;
  };
  featureHeader?: string;
  features?: { key: string; content: React.ReactNode }[];
  footerItems?: FooterItem[];
  'data-testid'?: string;
};

export const SocialConsentModal: FC<SocialConsentModalProps> = ({
  isOpen = false,
  close,
  description = '',
  featureHeader = '',
  features = [],
  platformIcon = 'default-icon',
  title = '',
  previewImage = { src: '', srcSet: '', alt: '' },
  'data-testid': dataTestId = 'social-consent-modal',
  footerItems = [],
  buttonsConfig = [],
}) => {
  return (
    <Modal
      title=""
      data-testid={dataTestId}
      isHeaderHidden
      noPadding
      isOpen={isOpen}
      modalSize="medium"
      handleToggleClick={close}
      buttons={buttonsConfig}
    >
      <FlexCol gap="8" className="cn_mx-8 cn_mt-8">
        <FlexRow justifyContent="center">
          <TextDynamic
            fontSize="h1"
            fontWeight="semibold"
            color="default"
            align="left"
            className="cn_w-full"
            mdProps={{ fontSize: 'xl', fontWeight: 'semibold', align: 'center', className: 'cn_w-4/5' }}
          >
            <Icon name={platformIcon} size="xl" className="mr-2" />
            {title}
          </TextDynamic>
          <BasicContainer className="cn_mt-4">
            <BodyText>{description}</BodyText>
          </BasicContainer>
        </FlexRow>
        <GridDynamic
          columns="1fr"
          gap="8"
          mdProps={{ columns: 'repeat(2, 1fr)', className: 'cn_mt-4 cn_mx-8' }}
          alignItems="center"
        >
          <BasicContainer gridRowStart={2} smProps={{ gridRowStart: undefined }}>
            <img
              src={previewImage.src}
              srcSet={previewImage.srcSet}
              alt={previewImage.alt}
              className="cn_w-full"
              data-testid="preview-image"
            />
          </BasicContainer>
          <FlexCol
            className="cn_atom_rounded-xl cn_atom_p-4 cn_h-full"
            bgColor="discountOfferBg"
            wrap="nowrap"
            gap="4"
            data-testid="social-features"
            gridRowStart={1}
            smProps={{ gridRowStart: undefined }}
          >
            <BodyText>{featureHeader}</BodyText>
            {features.map((item) => (
              <FeatureListItemTemplate key={item.key} item={item.content} />
            ))}
          </FlexCol>
        </GridDynamic>
        <FlexRow gap="8" mdProps={{ mx: '8' }} data-testid="social-footer">
          {footerItems.map((item) => (
            <FooterItemTemplate key={item.key} item={item} />
          ))}
        </FlexRow>
      </FlexCol>
    </Modal>
  );
};
